import React, { useState } from 'react';
import s from './VerifyEmailModal.module.css';
import Modal from '../../../../shared/ui/modal/Modal';
import Button from '../../../../shared/ui/button/Button';
import IconFullLeftArrow from '../../../../shared/ui/icons/IconFullLeftArrow';

interface VerifyEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (code: string) => Promise<void> | void;
    onResendCode: () => Promise<void> | void;
    isLoading: boolean;
}

const VerifyEmailModal: React.FC<VerifyEmailModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    onResendCode,
    isLoading,
}) => {
    const [codeArray, setCodeArray] = useState<string[]>(Array(6).fill(''));
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleChange = (index: number, value: string) => {
        if (/^[0-9]?$/.test(value)) {
            const newCode = [...codeArray];
            newCode[index] = value;
            setCodeArray(newCode);

            if (value && index < 5) {
                const nextInput = document.getElementById(`code-input-${index + 1}`);
                nextInput?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text').slice(0, 6).split('');
        if (pasted.every((c) => /^[0-9]$/.test(c))) {
            const newCode = Array(6).fill('');
            pasted.forEach((char, i) => (newCode[i] = char));
            setCodeArray(newCode);
        }
    };

    const handleVerifyCode = async () => {
        const code = codeArray.join('');
        if (code.length !== 6) return;

        try {
            setError(null);
            await onConfirm(code);
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
        }
    };

    const handleResend = async () => {
        try {
            setResendLoading(true);
            setResendSuccess(null);
            setResendError(null);
            await onResendCode();
            setResendSuccess('Code resent successfully!');
        } catch {
            setResendError('Failed to resend code.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} width="561px">
            <form
                className={s.form}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleVerifyCode();
                }}
            >
                <p className={s.title}>Verification code</p>

                <div className={s.subtitleWarpper}>
                    <span className={s.subtitle}>
                        Enter the 6-digit code sent to your new email
                    </span>
                    <div className={s.gotoemail}>
                        <a
                            href="https://mail.google.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.link}
                        >
                            Go to my email
                        </a>
                    </div>
                </div>

                <div className={s.codeInputContainer}>
                    {codeArray.map((digit, index) => (
                        <input
                            key={index}
                            id={`code-input-${index}`}
                            readOnly={success}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onPaste={handlePaste}
                            className={s.codeInput}
                        />
                    ))}
                </div>

                {error && <p className={s.error}>{error}</p>}

                <div className={s.resend_code_container}>
                    <p className={s.text}>Didn’t receive the code?</p>
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendLoading || isLoading || success}
                        className={s.link}
                    >
                        {resendLoading ? 'Resending...' : 'Resend code'}
                    </button>
                    {resendSuccess && <p className={s.successMsg}>{resendSuccess}</p>}
                    {resendError && <p className={s.errorMsg}>{resendError}</p>}
                </div>

                <Button
                    text={isLoading ? 'Verifying...' : 'Continue'}
                    onClick={handleVerifyCode}
                    state={codeArray.join('').length === 6 && !isLoading ? 'enabled' : 'disabled'}
                    icon={<IconFullLeftArrow />}
                    iconPosition="right"
                    style={{
                        background: '#FFDD35',
                        borderRadius: '26px',
                        fontWeight: 600,
                        height: '40px',
                        marginTop: '20px',
                    }}
                />
            </form>
        </Modal>
    );
};

export default VerifyEmailModal;
